import { ReactNode } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { usePlatformAdmin } from '@/contexts/PlatformAdminContext';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2, ShieldAlert, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface AdminProtectedRouteProps {
  children: ReactNode;
}

export const AdminProtectedRoute = ({ children }: AdminProtectedRouteProps) => {
  const { user, loading: authLoading, signOut } = useAuth();
  const { isPlatformAdmin, isCheckingAdmin } = usePlatformAdmin();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await signOut();
    navigate('/master');
  };

  // Carregando autenticação ou verificação de admin
  if (authLoading || isCheckingAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <div className="text-center space-y-4">
          <Loader2 className="h-12 w-12 animate-spin text-amber-500 mx-auto" />
          <p className="text-slate-400">Verificando permissões...</p>
        </div>
      </div>
    );
  }

  // Usuário não autenticado - redirecionar para /master
  if (!user) {
    return <Navigate to="/master" replace />;
  }

  // Usuário não é Platform Admin - Página 403
  if (!isPlatformAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <div className="text-center space-y-6 max-w-md p-8">
          <div className="mx-auto w-20 h-20 rounded-full bg-red-500/10 flex items-center justify-center">
            <ShieldAlert className="h-10 w-10 text-red-500" />
          </div>
          <div className="space-y-2">
            <h1 className="text-3xl font-bold text-white">403</h1>
            <h2 className="text-xl font-semibold text-slate-300">Acesso Proibido</h2>
          </div>
          <p className="text-slate-400">
            Você não possui permissão para acessar a área administrativa da plataforma.
          </p>
          <p className="text-sm text-slate-500">
            Se você acredita que isso é um erro, entre em contato com o suporte GOnX.
          </p>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Sair
          </Button>
        </div>
      </div>
    );
  }

  // Usuário é Platform Admin - renderizar conteúdo
  return <>{children}</>;
};
